/**
 * Manual order print areas (Phase 104, BLU-1450): the ONE home of the fixed
 * area list (D-09), its default platen geometry (D-25), the DPI classifier
 * (D-10) and the area text the QR label prints.
 *
 * Read by the placement editor (every keystroke, so everything here is pure,
 * O(1) and allocates at most one small object per call), the server compose
 * step that renders the 300 DPI print-ready PNG, and the label.
 *
 * Coordinates are inches on the platen, origin at the platen's top-left
 * corner, x to the right, y down. A placement is the design's top-left corner
 * plus its printed width; the height always follows from the trimmed
 * artwork's aspect (height / width), which never changes (aspect is locked).
 *
 * Geometry is authored for the 14x16 platen (D-25, editable later in code:
 * change the numbers in MANUAL_PRINT_AREAS). For another platen it scales:
 *   width = min(widthIn x platenW / 14, platenW - 0.5)
 *   top   = topIn x platenH / 16
 *   the right-of-centre offset (1.0 in) scales by platenW / 14.
 */
import { DTG_PLATEN_CODES, PLATEN_DIMS } from '../data/rip-routing.js';
/** The platen the geometry is authored for. */
const AUTHORED_W_IN = 14;
const AUTHORED_H_IN = 16;
/** Left edge of a right-of-centre area sits this far right of centre on 14x16. */
const RIGHT_OF_CENTRE_OFFSET_IN = 1.0;
/** Auto width always leaves this much of the platen width free. */
const AUTO_SIDE_MARGIN_IN = 0.5;
/** Smallest printed width a placement may shrink to. */
export const MANUAL_MIN_PRINT_WIDTH_IN = 0.5;
/** Print resolution of every platen file. */
const PLATEN_DPI = 300;
export const MANUAL_PRINT_AREAS = {
    left_chest: { side: 'front', labelUpper: 'LEFT CHEST', i18nKey: 'spots.area.left_chest', widthIn: 3.5, topIn: 1.0, anchor: 'right_of_centre' },
    centre_chest: { side: 'front', labelUpper: 'CENTRE CHEST', i18nKey: 'spots.area.centre_chest', widthIn: 6, topIn: 1.5, anchor: 'centre' },
    full_front: { side: 'front', labelUpper: 'FULL FRONT', i18nKey: 'spots.area.full_front', widthIn: 11, topIn: 1.0, anchor: 'centre' },
    pocket: { side: 'front', labelUpper: 'POCKET', i18nKey: 'spots.area.pocket', widthIn: 3, topIn: 1.5, anchor: 'right_of_centre' },
    full_back: { side: 'back', labelUpper: 'FULL BACK', i18nKey: 'spots.area.full_back', widthIn: 12, topIn: 1.5, anchor: 'centre' },
    upper_back: { side: 'back', labelUpper: 'UPPER BACK', i18nKey: 'spots.area.upper_back', widthIn: 10, topIn: 1.0, anchor: 'centre' },
    neck: { side: 'back', labelUpper: 'NECK', i18nKey: 'spots.area.neck', widthIn: 3, topIn: 0.5, anchor: 'centre' },
};
/** Every area code, in picker order (front areas, then back areas). */
export const MANUAL_PRINT_AREA_CODES = [
    'left_chest',
    'centre_chest',
    'full_front',
    'pocket',
    'full_back',
    'upper_back',
    'neck',
];
const FRONT_AREAS = MANUAL_PRINT_AREA_CODES.filter((a) => MANUAL_PRINT_AREAS[a].side === 'front');
const BACK_AREAS = MANUAL_PRINT_AREA_CODES.filter((a) => MANUAL_PRINT_AREAS[a].side === 'back');
/** The areas a side offers, in picker order. Returns a shared readonly list. */
export function areasForSide(side) {
    return side === 'front' ? FRONT_AREAS : BACK_AREAS;
}
/** Type guard for an area code read from untrusted input (draft JSON, DB rows). */
export function isManualPrintAreaCode(value) {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(MANUAL_PRINT_AREAS, value);
}
/** Platen size in inches, from the shared platen list (300 DPI pixel dims). */
export function platenSizeIn(platen) {
    const d = PLATEN_DIMS[platen];
    return { w: d.w / PLATEN_DPI, h: d.h / PLATEN_DPI };
}
/**
 * Platen size in inches from a pixel size at 300 DPI (Phase 91): the Upload
 * Studio draws the org's own Large platen pixels, which need not be
 * PLATEN_DIMS. Manual orders keep `platenSizeIn(platen)`.
 */
export function platenSizeInFromPx(px) {
    return { w: px.w / PLATEN_DPI, h: px.h / PLATEN_DPI };
}
function safeAspect(aspect) {
    return Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
}
function clampNumber(v, lo, hi) {
    if (!Number.isFinite(v))
        return lo;
    return v < lo ? lo : v > hi ? hi : v;
}
/** The one clamp every public path uses; returns the only object it allocates. */
function clampTo(x, y, width, aspect, pw, ph) {
    const maxW = Math.min(pw, ph / aspect);
    const w = clampNumber(width, Math.min(MANUAL_MIN_PRINT_WIDTH_IN, maxW), maxW);
    return {
        x_in: clampNumber(x, 0, pw - w),
        y_in: clampNumber(y, 0, ph - w * aspect),
        width_in: w,
    };
}
/**
 * `autoPlacement` on a platen given by its inch size. The area geometry is
 * authored for 14x16 and scales by the platen's width and height.
 */
export function autoPlacementIn(area, aspect, sizeIn) {
    const def = MANUAL_PRINT_AREAS[area];
    const a = safeAspect(aspect);
    const pw = sizeIn.w;
    const ph = sizeIn.h;
    const sx = pw / AUTHORED_W_IN;
    const top = (def.topIn * ph) / AUTHORED_H_IN;
    const width = Math.min(def.widthIn * sx, pw - AUTO_SIDE_MARGIN_IN, (ph - top) / a);
    const x = def.anchor === 'centre' ? (pw - width) / 2 : pw / 2 + RIGHT_OF_CENTRE_OFFSET_IN * sx;
    return clampTo(x, top, width, a, pw, ph);
}
/**
 * The default placement for an area (D-25): auto-sized to the area's width,
 * the design's TOP edge at the area's gap from the platen top, centred or
 * right of centre. Artwork too tall to fit below that gap is shrunk so the top
 * stays put. `aspect` = trimmed height / trimmed width.
 */
export function autoPlacement(area, aspect, platen) {
    return autoPlacementIn(area, aspect, platenSizeIn(platen));
}
/** `clampPlacement` on a platen given by its inch size. */
export function clampPlacementIn(placement, aspect, sizeIn) {
    return clampTo(placement.x_in, placement.y_in, placement.width_in, safeAspect(aspect), sizeIn.w, sizeIn.h);
}
/**
 * Keep the whole design (width x aspect height) on the platen and its width at
 * or above half an inch. The aspect never changes: an oversize design is
 * scaled down as a whole, then moved back inside.
 */
export function clampPlacement(placement, aspect, platen) {
    return clampPlacementIn(placement, aspect, platenSizeIn(platen));
}
/** `centrePlacement` on a platen given by its inch size. */
export function centrePlacementIn(placement, sizeIn) {
    return {
        x_in: Math.max(0, (sizeIn.w - placement.width_in) / 2),
        y_in: placement.y_in,
        width_in: placement.width_in,
    };
}
/** Centre the design across the platen; top and width unchanged ("Centre it"). */
export function centrePlacement(placement, platen) {
    return centrePlacementIn(placement, platenSizeIn(platen));
}
// ---------------------------------------------------------------------------
// Upload Studio tools (Phase 91, UI-SPEC keys F / T / S). Inch sizes only,
// never a platen code: the studio draws the org's own Large pixels. Each is
// O(1) and returns one object.
// ---------------------------------------------------------------------------
/**
 * Fit (key F): as wide as the platen allows at the current top, centred left
 * to right. A top so low that even the smallest width will not fit moves up
 * only as far as needed.
 */
export function fitPlacementIn(placement, aspect, sizeIn) {
    const a = safeAspect(aspect);
    const pw = sizeIn.w;
    const ph = sizeIn.h;
    const maxW = Math.min(pw, ph / a);
    const top = clampNumber(placement.y_in, 0, ph);
    const w = clampNumber(Math.min(pw, (ph - top) / a), Math.min(MANUAL_MIN_PRINT_WIDTH_IN, maxW), maxW);
    return { x_in: (pw - w) / 2, y_in: clampNumber(top, 0, ph - w * a), width_in: w };
}
/**
 * Top (key T): the design's top at the Full front (front) or Full back (back)
 * area's gap from the platen top, scaled by platen height (the Manual order
 * area default); left edge and width kept, then clamped.
 */
export function topPlacementIn(placement, aspect, side, sizeIn) {
    const area = side === 'front' ? MANUAL_PRINT_AREAS.full_front : MANUAL_PRINT_AREAS.full_back;
    const top = (area.topIn * sizeIn.h) / AUTHORED_H_IN;
    return clampTo(placement.x_in, top, placement.width_in, safeAspect(aspect), sizeIn.w, sizeIn.h);
}
/**
 * Same as (key S): another platen's placement carried over. Width and the
 * offset from the centre line scale by the width ratio, the top by the height
 * ratio, then clamped. A start point only; each platen is still saved alone.
 */
export function sameAsPlacementIn(from, aspect, fromSizeIn, toSizeIn) {
    const kw = toSizeIn.w / fromSizeIn.w;
    const kh = toSizeIn.h / fromSizeIn.h;
    const width = from.width_in * kw;
    const centreOffset = (from.x_in + from.width_in / 2 - fromSizeIn.w / 2) * kw;
    const x = toSizeIn.w / 2 + centreOffset - width / 2;
    return clampTo(x, from.y_in * kh, width, safeAspect(aspect), toSizeIn.w, toSizeIn.h);
}
/** Where a new design starts in the studio: the Full front / Full back auto placement. */
export function studioStartPlacementIn(side, aspect, sizeIn) {
    return autoPlacementIn(side === 'front' ? 'full_front' : 'full_back', aspect, sizeIn);
}
/**
 * The platen whose Large pixels equal the file's exactly (a file that is
 * already a full platen canvas, UI-SPEC "Wrong pixels"), or null. When it is
 * the open platen the studio places it edge to edge
 * ({ x_in: 0, y_in: 0, width_in: sizeIn.w }).
 */
export function fullPlatenFileMatch(filePx, platensPx) {
    for (const platen of DTG_PLATEN_CODES) {
        const large = platensPx[platen]?.large;
        if (large && large.width === filePx.width && large.height === filePx.height)
            return platen;
    }
    return null;
}
/** DTF / DTG dots per inch at the printed width, rounded. 0 for a non-positive width. */
export function placementDpi(trimmedWidthPx, widthIn) {
    if (!(widthIn > 0) || !Number.isFinite(trimmedWidthPx))
        return 0;
    return Math.round(trimmedWidthPx / widthIn);
}
/** D-10 thresholds. Warn only, never block. */
export const DPI_SHARP_MIN = 200;
export const DPI_OK_MIN = 150;
/** >= 200 Sharp, 150-199 OK, < 150 Blurry. */
export function classifyDpi(dpi) {
    if (dpi >= DPI_SHARP_MIN)
        return 'sharp';
    if (dpi >= DPI_OK_MIN)
        return 'ok';
    return 'blurry';
}
/**
 * The print-area lines the QR label shows, e.g. `['FRONT LEFT CHEST',
 * 'BACK FULL BACK']`: front spots first, then back, each in the order given.
 * Uppercase English on purpose (the label is not translated). An unknown area
 * code read from stored data is skipped rather than printed raw.
 */
export function formatPrintAreaLines(spots) {
    const out = [];
    for (const side of ['front', 'back']) {
        for (const spot of spots) {
            if (spot.side !== side || !isManualPrintAreaCode(spot.area))
                continue;
            out.push(`${side.toUpperCase()} ${MANUAL_PRINT_AREAS[spot.area].labelUpper}`);
        }
    }
    return out;
}
